import React from 'react';
import { 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetClose
} from '../../../components/ui/sheet';
import { navigationConfig, adminNavigation } from '../config/navigation';
import { getNavIcon } from '../config/navIcons';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  X,
  AlertCircle
} from 'lucide-react';
import { CurrentPage } from '../../../types';
import { useMetaAccountHealth } from '../../../hooks/useMetaAccountHealth';

interface MobileMenuContentProps {
  currentPage: CurrentPage;
  onNavigate: (page: CurrentPage) => void;
}

/**
 * Mobile Menu Content using Radix Sheet.
 * SOLID: Responsibility is rendering the navigation list for mobile.
 */
export const MobileMenuContent: React.FC<MobileMenuContentProps> = ({ 
  currentPage, 
  onNavigate 
}) => {
  const { isAdmin } = useAuth();
  const { hasCriticalMetaHealth } = useMetaAccountHealth();

  const fullNavigation = [...navigationConfig];
  if (isAdmin()) {
    fullNavigation.push(...adminNavigation);
  }

  const sections = [
    { label: 'Principal', items: fullNavigation.filter(i => i.category === 'main') },
    { label: 'Componentes', items: fullNavigation.filter(i => i.category === 'components') },
    { label: 'Configuración', items: fullNavigation.filter(i => i.category === 'config') },
    { label: 'Pagos', items: fullNavigation.filter(i => i.category === 'payment') },
    { label: 'Administración', items: fullNavigation.filter(i => i.category === 'admin') },
  ];

  return (
    <SheetContent side="left" className="w-[85%] sm:max-w-xs p-0 border-none bg-white shadow-2xl">
      <div className="h-full flex flex-col">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-100 px-4 pt-12 pb-6">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <img 
                src="/assets/logo_reveca_def.png" 
                alt="Reveca" 
                className="w-[120px] h-auto object-contain rounded-lg shadow-sm relative z-50 block"
              />
            </div>
          </div>
        </div>

        {/* Navigation List */}
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-8">
          {sections.map(section => section.items.length > 0 && (
            <div key={section.label}>
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4 px-3">
                {section.label}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.id}>
                    <SheetClose asChild>
                      <button
                        onClick={() => onNavigate(item.id as CurrentPage)}
                        className={`w-full flex items-center px-4 py-3 rounded-xl text-left transition-all duration-200 group ${
                          currentPage === item.id
                            ? 'bg-[#E8F7F7] text-[#0D7377] font-semibold'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`transition-colors ${
                          currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400 group-hover:text-gray-600'
                        }`}>
                          {getNavIcon(item.icon)}
                        </span>
                        <span className="ml-4 text-sm">{item.name}</span>
                        {item.id === 'whatsapp' && hasCriticalMetaHealth && (
                          <AlertCircle className="h-3.5 w-3.5 text-red-500 animate-pulse ml-1.5 shrink-0" aria-hidden />
                        )}
                        {currentPage === item.id && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#1CB3B3]" />
                        )}
                      </button>
                    </SheetClose>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer info */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/50">
          <p className="text-xs text-gray-400 text-center uppercase tracking-widest font-medium">
            Versión 1.0.0
          </p>
        </div>
      </div>
    </SheetContent>
  );
};
